import { useState, type MouseEvent, type ReactNode } from "react";

import type { GeoBusinessCandidate, GeoBusinessVerifyResult, GeoOperationStatus } from "./types";
import "./businessGeo.css";

type GeoOperationPanelProps = {
  status: GeoOperationStatus;
  result?: GeoBusinessVerifyResult;
  onRetry?: () => void;
  onSelectCandidate?: (candidate: GeoBusinessCandidate) => void;
  children?: ReactNode;
};

const statusLabels: Record<GeoOperationStatus, string> = {
  idle: "未识别",
  processing: "识别中",
  confirmed: "已确认",
  needs_correction: "待修正",
  retryable_failure: "失败可重试",
};

const MAX_SELECTABLE_CANDIDATES = 10;

/** 业务编辑页统一展示 GEO 状态与事实证据。 */
export function GeoOperationPanel({
  status,
  result,
  onRetry,
  onSelectCandidate,
  children,
}: GeoOperationPanelProps) {
  const [adoptedKey, setAdoptedKey] = useState<string>();
  const selectableCandidates = status === "needs_correction" && onSelectCandidate
    ? (result?.candidates ?? []).slice(0, MAX_SELECTABLE_CANDIDATES)
    : [];
  const selectedCandidate = selectableCandidates.find((candidate) => candidateKey(candidate) === adoptedKey);
  const displayResult = selectedCandidate && result
    ? {
      ...result,
      amapStandardAddress: selectedCandidate.address || selectedCandidate.name,
      longitude: selectedCandidate.longitude,
      latitude: selectedCandidate.latitude,
      coordinateSystem: selectedCandidate.longitude === undefined ? result.coordinateSystem : "GCJ-02",
    }
    : result;
  const llmConclusion = displayResult?.llmConclusion || displayResult?.conclusion || "-";

  /** 采用候选后立刻更新面板证据，并回填业务表单。 */
  function handleAdopt(candidate: GeoBusinessCandidate) {
    setAdoptedKey(candidateKey(candidate));
    onSelectCandidate?.(candidate);
  }

  return (
    <section className="business-geo-panel" aria-live="polite" aria-label="GEO 识别结果">
      <div className={`business-geo-panel__status business-geo-panel__status--${status}`}>
        {statusLabels[status]}
      </div>
      {status === "processing" ? <div className="business-geo-panel__message">正在整理资料并核验高德地址，请稍候。</div> : null}
      {displayResult ? (
        <div className="business-geo-panel__facts">
          <div><span>LLM 结论</span><strong>{llmConclusion}</strong></div>
          <div>
            <span>高德标准地址</span>
            <strong aria-label="高德标准地址">{displayResult.amapStandardAddress || "-"}</strong>
          </div>
          <div><span>坐标</span><strong>{formatCoordinates(displayResult)}</strong></div>
          <div><span>置信度</span><strong>{`${Math.round(displayResult.confidenceScore * 100)}%`}</strong></div>
          {displayResult.correctionReason ? <div className="business-geo-panel__reason"><span>待修正原因</span><strong>{displayResult.correctionReason === llmConclusion ? "同上" : displayResult.correctionReason}</strong></div> : null}
        </div>
      ) : null}
      {selectableCandidates.length ? (
        <ul className="business-geo-panel__candidates" aria-label="高德候选">
          {selectableCandidates.map((candidate) => {
            const selected = candidateKey(candidate) === adoptedKey;
            return (
              <li
                key={candidateKey(candidate)}
                className={`business-geo-panel__candidate${selected ? " business-geo-panel__candidate--selected" : ""}`}
              >
                <div>
                  <strong>{candidate.name}</strong>
                  <span>{candidate.address || "-"}</span>
                </div>
                {selected ? (
                  <span className="business-geo-panel__adopted">已采用</span>
                ) : (
                  <button
                    type="button"
                    className="business-geo-panel__adopt"
                    aria-label={`采用 ${candidate.name}`}
                    onMouseDown={keepDrawerFocus}
                    onClick={() => handleAdopt(candidate)}
                  >
                    采用
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
      {children}
      {status === "retryable_failure" && onRetry ? (
        <button type="button" className="business-geo-panel__retry" onClick={onRetry}>重试 GEO</button>
      ) : null}
    </section>
  );
}

/** 格式化统一 GCJ-02 坐标展示。 */
function formatCoordinates(result: GeoBusinessVerifyResult) {
  if (result.longitude === undefined || result.latitude === undefined) return "-";
  return `${result.longitude.toFixed(6)}, ${result.latitude.toFixed(6)}（${result.coordinateSystem}）`;
}

/** 用 POI、名称和坐标组成候选列表稳定键。 */
function candidateKey(candidate: GeoBusinessCandidate) {
  return [candidate.provider, candidate.poiId, candidate.name, candidate.longitude, candidate.latitude].join(":");
}

/** 避免 Arco Drawer focusLock 吞掉采用按钮的 click。 */
function keepDrawerFocus(event: MouseEvent<HTMLButtonElement>) {
  event.preventDefault();
}
